import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useCreateService, useUpdateService, useNextServiceCode } from '@/hooks/queries/useServices';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/components/ui/toast/useToast';
import type { Service } from '@lebusiness/types';

const serviceSchema = z.object({
  name: z.string().min(1, 'الاسم مطلوب / Nom requis'),
  category: z.string().min(1, 'الفئة مطلوبة / Catégorie requise'),
  unit: z.string().min(1, 'الوحدة مطلوبة / Unité requise'),
  salePrice: z.number().min(0, 'السعر يجب أن يكون موجب / Prix doit être positif'),
  vat: z.number().min(0).max(100),
  code: z.string().optional(),
  description: z.string().optional(),
});

type ServiceFormData = z.infer<typeof serviceSchema>;

interface ServiceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service?: Service | null;
  onSuccess?: () => void;
}

export function ServiceForm({ open, onOpenChange, service, onSuccess }: ServiceFormProps) {
  const { company } = useAuthStore();
  const createService = useCreateService();
  const updateService = useUpdateService();
  const { data: nextCode } = useNextServiceCode(company?.id || '', 'SRV');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ServiceFormData>({
    resolver: zodResolver(serviceSchema),
    defaultValues: service
      ? {
          code: service.code,
          name: service.name,
          category: service.category,
          unit: service.unit,
          salePrice: service.salePrice,
          vat: service.vat,
          description: service.description || '',
        }
      : {
          code: nextCode || 'SRV-001',
          category: 'Autres',
          unit: 'fixed',
          salePrice: 0,
          vat: 19,
        },
  });

  useEffect(() => {
    if (nextCode && !service) {
      setValue('code', nextCode);
    }
  }, [nextCode, service, setValue]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (service) {
        reset({
          code: service.code,
          name: service.name,
          category: service.category,
          unit: service.unit,
          salePrice: (service as any).sale_price || service.salePrice,
          vat: service.vat,
          description: service.description || '',
        });
      } else {
        reset({
          code: nextCode || 'SRV-001',
          name: '',
          category: 'Autres',
          unit: 'fixed',
          salePrice: 0,
          vat: 19,
          description: '',
        });
      }
    }
  }, [open, service, nextCode, reset]);

  const onSubmit = async (data: ServiceFormData) => {
    if (!company?.id) {
      toast.error('خطأ / Erreur', 'معرف الشركة مفقود / ID société manquant');
      return;
    }

    try {
      const serviceData = {
        company_id: company.id,
        code: data.code || nextCode || `SRV-${Date.now()}`,
        name: data.name,
        category: data.category,
        unit: data.unit,
        sale_price: data.salePrice,
        vat: data.vat,
        description: data.description || null,
        is_active: true,
      };

      if (service) {
        await updateService.mutateAsync({ id: service.id, ...serviceData });
        toast.success(
          'تم التحديث بنجاح / Modifié avec succès',
          'تم تحديث الخدمة / Le service a été modifié'
        );
      } else {
        await createService.mutateAsync(serviceData as any);
        toast.success(
          'تم الإضافة بنجاح / Ajouté avec succès',
          'تم إضافة الخدمة / Le service a été ajouté'
        );
      }

      onSuccess?.();
      onOpenChange(false);
      reset();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error(
        'خطأ / Erreur',
        error instanceof Error ? error.message : 'فشل حفظ الخدمة / Échec de l\'enregistrement'
      );
    }
  };

  const categories = [
    'Consulting',
    'IT & Technologie',
    'Transport',
    'Nettoyage',
    'Maintenance',
    'Formation',
    'Comptabilité',
    'Juridique',
    'Design',
    'Autres',
  ];

  const units = [
    { value: 'hour', label: 'ساعة / Heure' },
    { value: 'day', label: 'يوم / Jour' },
    { value: 'project', label: 'مشروع / Projet' },
    { value: 'session', label: 'جلسة / Séance' },
    { value: 'fixed', label: 'مقطوع / Forfait' },
    { value: 'month', label: 'شهر / Mois' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {service
              ? 'تعديل الخدمة / Modifier le service'
              : 'خدمة جديدة / Nouveau service'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">الاسم / Nom *</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="مثال: استشارة محاسبية / Ex: Consultation comptable"
            />
            {errors.name && (
              <p className="text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          {/* Category + Unit */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">الفئة / Catégorie *</Label>
              <Select
                value={watch('category')}
                onValueChange={(value) => setValue('category', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.category && (
                <p className="text-sm text-red-600">{errors.category.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit">وحدة الفوترة / Unité de facturation *</Label>
              <Select
                value={watch('unit')}
                onValueChange={(value) => setValue('unit', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.unit && (
                <p className="text-sm text-red-600">{errors.unit.message}</p>
              )}
            </div>
          </div>

          {/* Sale Price + VAT */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="salePrice">السعر / Prix * (DA)</Label>
              <Input
                id="salePrice"
                type="number"
                step="0.01"
                {...register('salePrice', { valueAsNumber: true })}
                placeholder="0.00"
              />
              {errors.salePrice && (
                <p className="text-sm text-red-600">{errors.salePrice.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="vat">TVA *</Label>
              <Select
                value={watch('vat')?.toString()}
                onValueChange={(value) => setValue('vat', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0% (Exonéré)</SelectItem>
                  <SelectItem value="9">9% (Taux réduit)</SelectItem>
                  <SelectItem value="19">19% (Taux normal)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Code (optional) */}
          <div className="space-y-2">
            <Label htmlFor="code">
              الرمز / Code
              <span className="text-xs text-gray-500 ml-2">(يتم إنشاؤه تلقائياً / Auto-généré)</span>
            </Label>
            <Input id="code" {...register('code')} placeholder="SRV-001" />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">الوصف / Description</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="وصف الخدمة... / Description du service..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              إلغاء / Annuler
            </Button>
            <Button
              type="submit"
              disabled={createService.isPending || updateService.isPending}
              className="bg-gradient-to-r from-blue-500 to-indigo-600"
            >
              {(createService.isPending || updateService.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {service ? 'حفظ / Enregistrer' : 'إضافة / Ajouter'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
